import type { ReactNode } from 'react';
import { useRef } from 'react';
import { styled } from 'styled-components';

import { Box } from 'components/Box/Box';
import { ExpandButton } from 'components/ExpandButton/ExpandButton';
import { useTrackLinks } from 'hooks/useTrackLinks';
import { fontSizesByType, legacySpace } from 'theme/theme';

import { ListingRichText } from './ListingRichText';
import { ListingRichTextTruncatedHtml } from './ListingRichTextTruncatedHtml';

const PrintBox = styled(Box)<{ $printDisplay: string }>`
  @media print {
    display: ${(props) => props.$printDisplay};
  }
`;

const Container = styled.div<{ $showAll?: boolean }>`
  ${(props) =>
    props.$showAll
      ? `
    overflow-anchor: none;
  `
      : ''}
`;

type Props = {
  html: string;
  truncatedHtml: string;
  showMoreLinkTitle: string;
  showLessLinkTitle: string;
  renderHiddenLink?: () => ReactNode;
  eventName?: string;
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  eventProperties?: Record<string, any>;
  alwaysTruncate?: boolean;
  onLinkClick?: (link: HTMLAnchorElement) => void;
  onToggle?: (isOpen: boolean) => void;
  toggleQAId?: string;
};

export function ListingRichTextTruncatedHtmlWithLinks({
  html,
  truncatedHtml,
  showMoreLinkTitle,
  showLessLinkTitle,
  renderHiddenLink,
  eventName,
  eventProperties,
  alwaysTruncate,
  onLinkClick,
  onToggle,
  toggleQAId,
}: Props) {
  const ref = useRef<HTMLDivElement>(null);
  const truncatedRef = useRef<HTMLDivElement>(null);

  useTrackLinks({
    ref,
    eventName,
    eventProperties,
    onClick: onLinkClick,
  });

  useTrackLinks({
    ref: truncatedRef,
    eventName,
    eventProperties,
    onClick: onLinkClick,
  });

  return (
    <ListingRichTextTruncatedHtml
      html={html}
      truncatedHtml={truncatedHtml}
      alwaysTruncate={alwaysTruncate}
      render={({
        truncated,
        truncatedHtml: truncatedHtmlForRender,
        showAll,
        toggleShowAll,
      }) => (
        <Container $showAll={showAll}>
          <PrintBox display={showAll ? 'block' : 'none'} $printDisplay="block">
            <div ref={ref}>
              <ListingRichText content={html} />
            </div>
          </PrintBox>

          <PrintBox display={showAll ? 'none' : 'block'} $printDisplay="none">
            <div ref={truncatedRef}>
              <ListingRichText content={truncatedHtmlForRender} />
            </div>
          </PrintBox>

          <PrintBox $printDisplay="none">
            {truncated ? (
              <Box display="flex" flexWrap="wrap">
                <Box
                  width={[1, 1, 1, 'auto']}
                  fontSize={fontSizesByType.body}
                  marginTop={legacySpace[3]}
                >
                  <ExpandButton
                    onClick={() => {
                      toggleShowAll();
                      if (onToggle) onToggle(!showAll);
                    }}
                    open={showAll}
                    qaId={toggleQAId}
                  >
                    {showAll ? showLessLinkTitle : showMoreLinkTitle}
                  </ExpandButton>
                </Box>

                {renderHiddenLink && showAll && (
                  <Box
                    width={[1, 1, 1, 'auto']}
                    ml={[0, 0, 0, 'auto']}
                    mt={[4, 4, 4, 0]}
                  >
                    {renderHiddenLink()}
                  </Box>
                )}
              </Box>
            ) : (
              <div style={{ display: 'flex' }}>
                {renderHiddenLink && (
                  <Box ml={[0, 0, 0, 'auto']}>{renderHiddenLink()}</Box>
                )}
              </div>
            )}
          </PrintBox>
        </Container>
      )}
    />
  );
}
